import type { AnalyticsEventPayload, AnalyticsProvider } from '@/services/analytics';
import { AnalyticsEvents, type AnalyticsEventName } from '@/services/analytics/events';
import {
  getCookieConsentDomain,
  readCookieConsentFromDocument,
  type ICookieConsent,
} from '@/utils/cookieConsent.utils';
import { canLoadTracking } from '@/utils/tracking.utils';

const PIXEL_ID = 'EwSWTx8s8sDzvzgrfeTQna';
const PIXEL_URL = 'https://bzr.openai.com/v1/sdk/events';
// Shared with the website's openAiAdsAttribution.ts; matches the SDK's 30-day click cookie.
const CLICK_COOKIE = '__oppref';
const CLICK_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

const EVENTS: Partial<Record<AnalyticsEventName, { name: string; type: string }>> = {
  [AnalyticsEvents.auth.sign_up]: { name: 'registration_completed', type: 'customer_action' },
  [AnalyticsEvents.billing.trialStarted]: { name: 'trial_started', type: 'plan_enrollment' },
  [AnalyticsEvents.checkout.submit]: { name: 'checkout_started', type: 'contents' },
  [AnalyticsEvents.checkout.purchase]: { name: 'order_created', type: 'contents' },
};

function readClickReference(): string | undefined {
  const incoming = new URL(window.location.href).searchParams.get('oppref');
  let stored: string | undefined;
  try {
    const cookie = document.cookie
      .split(';')
      .map((entry) => entry.trim())
      .find((entry) => entry.startsWith(`${CLICK_COOKIE}=`));
    stored = cookie ? decodeURIComponent(cookie.slice(CLICK_COOKIE.length + 1)) : undefined;
  } catch {
    // The current landing URL still works if cookies are unavailable.
  }
  const value = incoming || stored;
  if (!value || encodeURIComponent(value).length > 3000) return;
  if (incoming && incoming !== stored) {
    const domain = getCookieConsentDomain(window.location.hostname);
    try {
      document.cookie = `${CLICK_COOKIE}=${encodeURIComponent(incoming)}; Path=/; Max-Age=${CLICK_MAX_AGE_SECONDS}; SameSite=Lax${domain ? `; Domain=${domain}` : ''}${window.location.protocol === 'https:' ? '; Secure' : ''}`;
    } catch {
      // Optional attribution storage must never interrupt the conversion.
    }
  }
  return value;
}

/** Image transport avoids loading the SDK, which can automatically inspect app forms.
 * https://developers.openai.com/ads/image-tag
 */
export class OpenAiAdsAnalytics implements AnalyticsProvider {
  public readonly name = 'openai-ads';
  public readonly consentCategories = ['marketing'] as const;
  private consentVersion = 0;

  constructor(private readonly pixelId = PIXEL_ID) {}

  public setConsent(consent: ICookieConsent | null): void {
    if (!consent?.marketing) this.consentVersion++;
  }

  public dispose(): void {
    this.consentVersion++;
  }

  public init(): void {
    readClickReference();
  }

  public trackPageview(): Promise<void> {
    return this.send('page_viewed', { type: 'contents' });
  }

  public trackEvent(name: AnalyticsEventName, payload?: AnalyticsEventPayload): Promise<void> | void {
    const event = EVENTS[name];
    if (!event) return;

    // Only documented conversion fields leave the app; no identity, form data or internal event context.
    const data: Record<string, string | number> = { type: event.type };
    if (
      event.type === 'contents' &&
      typeof payload?.amount_cents === 'number' &&
      Number.isSafeInteger(payload.amount_cents) &&
      payload.amount_cents >= 0 &&
      typeof payload.currency === 'string' &&
      /^[A-Z]{3}$/.test(payload.currency)
    ) {
      data.amount = payload.amount_cents;
      data.currency = payload.currency;
    }
    const transactionId =
      name === AnalyticsEvents.checkout.purchase && typeof payload?.transaction_id === 'string'
        ? payload.transaction_id
        : undefined;
    return this.send(event.name, data, transactionId);
  }

  private async send(name: string, data: Record<string, string | number>, transactionId?: string): Promise<void> {
    const consentVersion = this.consentVersion;
    // GET pixels must not contain raw order IDs. A stable digest preserves purchase deduplication.
    const id = transactionId
      ? Array.from(
          new Uint8Array(
            await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`digidoo:openai:purchase:${transactionId}`)),
          ),
        )
          .map((byte) => byte.toString(16).padStart(2, '0'))
          .join('')
      : crypto.randomUUID();

    // Hashing is asynchronous: recheck withdrawal, expiry and private URLs before starting a request.
    if (consentVersion !== this.consentVersion || !readCookieConsentFromDocument()?.marketing || !canLoadTracking())
      return;
    const url = new URL(PIXEL_URL);
    url.searchParams.set('pid', this.pixelId);
    url.searchParams.set('event', name);
    url.searchParams.set('event_id', id);
    const oppref = readClickReference();
    if (oppref) url.searchParams.set('oppref', oppref);
    for (const [key, value] of Object.entries(data)) url.searchParams.set(`data[${key}]`, String(value));

    const pixel = new Image(1, 1);
    // Even class IDs, auth paths and query parameters must not enter the HTTP Referer.
    pixel.referrerPolicy = 'origin';
    return new Promise((resolve, reject) => {
      pixel.onload = () => resolve();
      pixel.onerror = () => reject(new Error(`OpenAI Ads event failed: ${name}`));
      pixel.src = url.href;
    });
  }
}
